import { scaleLinear, scaleOrdinal } from 'd3-scale';
import { forceSimulation, forceCollide, forceX, forceY } from 'd3-force';
import { maxBy, max, get } from 'lodash';

export const NO_RECORD_POLICY = 'No Record';
export const MONITOR_POLICY = 'p=none';
export const QUARANTINE_POLICY = 'p=quarantine';
export const REJECT_POLICY = 'p=reject';

export const formatPercentageForChart = ( decimal ) => {
    let localeOptions = { maximumFractionDigits: 2, minimumFractionDigits: 2 };
    let locale = undefined; // Intenionally blank, assumes browser locale
    return ( decimal * 100 ).toLocaleString( locale, localeOptions );
};
export const percentAuthorizedPassingDmarc = ( row, dmarcPassType ) => {
    let totalVerifiedDmarcPassCount;
    if( dmarcPassType === 'double' ){
        totalVerifiedDmarcPassCount = get( row, 'verified.dmarc.doublePassCount', 0 );
    }
    else{
        totalVerifiedDmarcPassCount = get( row, 'verified.dmarc.singlePassCount', 0 ) + get( row, 'verified.dmarc.doublePassCount', 0 );
    }
    let totalVerifiedCount = get( row, 'verified.totalCount', 0 );

    let pct = totalVerifiedCount > 0 ? totalVerifiedDmarcPassCount / totalVerifiedCount : 1;

    // For domains without DMARC policies, we won’t know if there is any legitimate email or not, so display on left side.
    if( ( row.policy === 'no_record' || !row.policy ) && totalVerifiedCount === 0 ){
        pct = 0;
    }

    return pct;
};
export const dmarcPolicyToFriendly = ( row ) => {
    let policyDict = {
        no_record: NO_RECORD_POLICY,
        none: MONITOR_POLICY,
        null: NO_RECORD_POLICY, // eslint-disable-line quote-props
        quarantine: QUARANTINE_POLICY,
        reject: REJECT_POLICY
    };
    // row.policy will === null when no dmarc record found
    return policyDict[row.policy] || NO_RECORD_POLICY;
};

export const buildAbuseRate = ( row ) => {
    let totalMailCount = get( row, 'totalMailCount' );
    let abuseRate = totalMailCount > 0 ? get( row, 'unverified.totalCount', 0 ) / totalMailCount : 0;

    return abuseRate;
};

export const buildColorClass = ( row ) => {
    let abuseRate = row.abuseRate ? row.abuseRate : buildAbuseRate( row );
    let step = Math.ceil( abuseRate * 10 ) || 1;

    return `abuse-level-${step}`;
};

export const buildSizeScale = ( aggDomainsData ) => {
    let maxRow = maxBy( aggDomainsData, ( row ) => {
        return row.totalMailCount;
    } );

    let maxVal = ( maxRow && maxRow.totalMailCount > 0 ) ? maxRow.totalMailCount : 1; // Don't ever let max be zero
    let minRadius = 3;
    let maxRadius = 35;
    return scaleLinear()
        .domain( [0, maxVal] ) // Size of circle based off totalMailCount
        .range( [minRadius, maxRadius] );
};

export const buildXScale = ( width ) => {
    let xMinPos = 30;
    let xMaxPos = width - 150;
    return scaleLinear()
        .domain( [0, 1] ) // Percentage of verified mail passing DMARC, represented as decimal
        .range( [xMinPos, xMaxPos] );
};
export const buildYScale = ( height ) => {
    let step = height / 5;

    return scaleOrdinal()
        .domain( [NO_RECORD_POLICY, MONITOR_POLICY, QUARANTINE_POLICY, REJECT_POLICY, ''] )
        .range( [height - 1 * step, 3 * step, 2 * step, 1 * step, 0] );
};

export const buildStrengthScale = ( data ) => {
    let maxRow = maxBy( data, ( row ) => {
        return row.totalMailCount;
    } );

    let maxVal = maxRow ? maxRow.totalMailCount : 1;
    return scaleLinear()
        .domain( [0, maxVal] )
        .range( [0.5, 1] );
};

export const formatBubbleCaption = ( count ) => {
    let label = '';
    let oneMillion = 1000000;
    let oneBillion = 1000000000;
    count = count || 0;
    if( count / oneBillion >= 1 ){
        label = `${( count / oneBillion ).toPrecision( 3 ).toLocaleString()}b`;
    }
    else if( count / oneMillion >= 1 ){
        label = `${( count / oneMillion ).toPrecision( 3 ).toLocaleString()}m`;
    }
    else if( count / 1000 >= 1 ){
        label = `${( count / 1000 ).toPrecision( 3 ).toLocaleString()}k`;
    }
    else{
        label = `${Math.ceil( count )}`;
    }
    return label;
};
export const calculateLegendCaptionValues = ( aggDomainsData ) => {
    let counts = aggDomainsData.map( ( row ) => {
        return get( row, 'totalMailCount', 0 );
    } );
    let maximum = max( counts );
    let step = maximum / 4;
    return [step * 1, step * 2, step * 3, step * 4];
};

/* Applies D3 force simulation for bubble-chart clustering against given data,
 * expensive processing, invoke with care
 */
export const runSimulation = ( config, data ) => {
    const { height, width } = config;
    const xScale = buildXScale( width );
    const yScale = buildYScale( height );
    const sizeScale = buildSizeScale( data );

    let swimlaneAllowance = ( ( height / 5 ) / 2 ) - 10; // == how far (px) off the top/bottom of the line we bound the bubbles
    // Adjust bubbles to cluster in the swimlane, not just in a circle
    let boundForce = function(){
        let nodes;
        function force(){
            nodes.forEach( function( node ){
                const scaledY = yScale( node.friendlyPolicy );
                const scaledRadius = sizeScale( node.totalMailCount );
                const scaledDiameter = 2 * scaledRadius;
                const leftXPadding = 6;
                const leftXMinimum = scaledDiameter + leftXPadding;

                node.x = Math.max( leftXMinimum, Math.min( width - 150, node.x ) );
                node.y = Math.max(
                    scaledY - swimlaneAllowance + scaledRadius,
                    Math.min( scaledY + swimlaneAllowance - scaledRadius, node.y )
                );
            } );
        }

        force.initialize = function( nodeData ){
            nodes = nodeData;
        };

        return force;
    };

    let simulation = forceSimulation( data )
        .force( 'y', forceY( ( row ) => {
            return yScale( row.friendlyPolicy );
        } ) )
        .force( 'x', forceX( ( row ) => {
            return xScale( row.percentAuthorizedPassingDmarc );
        } ) )
        .force( 'bounding', boundForce() )
        .force( 'collide', forceCollide( ( row ) => {
            let padding = 0.5;
            return sizeScale( row.totalMailCount ) + padding;
        } ).strength( 1 ).iterations( 1 ) ) //more iterations == less overlap, at the cost of more processing time
        .stop();


    simulation.tick( 300 );

    return data;
};

/* Used in bubble-chart-worker.js to handle messages passed to worker */
export const bubbleChartWorkerOnMessage = function( event ){
    let { config, aggDomainsData: data } = event.data;
    runSimulation( config, data );
    return postMessage( data );
};
